import {captureMessage, withScope} from '@sentry/angular';
import {environment} from '@shared-types';

export async function hardResetApplication(RESET_SESSION_MESSAGE = '') {
	const RESET_REPORT = {
		timestamp: new Date(),
		localStorage: undefined, // 'cleared',
		sessionStorage: undefined, // 'cleared',
		cookies: undefined, // 'cleared',
		indexedDB: [], // 'deleted',
		caches: undefined, // 'cleared',
		serviceWorker: undefined, // 'unregistered',
	};
	const appLanguage = window.language() + '';
	try {
		withScope(scope => {
			scope.setExtras({
				environment,
				action: 'hard-reset-start',
			});
			scope.setTag('action', 'hard-reset-start');
			captureMessage('Hard Reset Application', 'info');
		});

		// clear all storages:
		if (window.localStorage) {
			localStorage.clear();
			localStorage.setItem('APP_RESET_LANG', appLanguage);
			RESET_REPORT.localStorage = 'cleared';
		}
		if (window.sessionStorage) {
			sessionStorage.clear();
			RESET_REPORT.sessionStorage = 'cleared';
		}

		try {
			// remove all cookies
			document.cookie.split(';').forEach(cookie => {
				const eqPos = cookie.indexOf('=');
				const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
				document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;';
			});
		} catch (error) {
			RESET_REPORT.cookies = 'cleared';
		}

		// clear IndexedDB databases:
		await window.indexedDB
			.databases()
			.then(dbs => {
				return Promise.all(
					dbs.map(async db => {
						if (db.name != 'sentry-offline') {
							window.indexedDB.deleteDatabase(db.name);
							RESET_REPORT.indexedDB = [...RESET_REPORT.indexedDB, db.name];
						}
					}),
				);
			})
			.catch(error => {
				console.error('Error deleting IndexedDB databases:', error);
				RESET_REPORT.indexedDB = [...RESET_REPORT.indexedDB, 'status:ERROR'];
				return false;
			});
		RESET_REPORT.indexedDB = [...RESET_REPORT.indexedDB, 'status:DELETED'];

		// clear the caches:
		await caches
			.keys()
			.then(cacheNames => {
				return Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
			})
			.catch(error => {
				console.error('Error clearing caches:', error);
				RESET_REPORT.caches = 'ERROR';
				return false;
			});
		RESET_REPORT.caches = 'cleared';

		// unregister service worker:
		if (navigator.serviceWorker) {
			await navigator.serviceWorker
				.getRegistration()
				.then(registration => {
					return Promise.all([registration?.unregister()]);
				})
				.catch(error => {
					console.error('Error unregistering service worker:', error);
					RESET_REPORT.serviceWorker = 'ERROR';
					return false;
				});
			RESET_REPORT.serviceWorker = 'unregistered';
		}
		console.log('Reset report:', RESET_REPORT);

		return new Promise(resolve => {
			setTimeout(() => {
				withScope(scope => {
					scope.setTag('action', 'hard-reset-complete');
					scope.setExtras({
						environment,
						resetResults: RESET_REPORT,
					});
					captureMessage(`Hard Reset Application Complete ${RESET_SESSION_MESSAGE ? `[${RESET_SESSION_MESSAGE}]` : ''}`, 'info');
					resolve(RESET_REPORT);
					console.debug('[DocCycle] Hard Reset Application Complete');
				});
			}, 1000); // delay to ensure the browser has time to update and the app
		});
	} catch (error) {
		console.error(`Error hard resetting application: `, error);
		withScope(scope => {
			scope.setTag('action', 'hard-reset-error');
			scope.setExtras({
				environment,
				error,
			});
			captureMessage(`Hard Reset Application Error ${RESET_SESSION_MESSAGE ? `[${RESET_SESSION_MESSAGE}]` : ''}`, 'error');
			console.error('[DocCycle] Hard Reset Application Error:', error);
		});
		return Promise.reject(error);
	}
}
